import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { UtilsService } from '../utils/utils.service';
import {
  getChannelSetupConfigUrl,
  getChannelSetupFilterTimingUrl,
  getChannelSetupBlacklistUrl,
  getChannelSetupBlacklistEntriesUrl,
  getChannelSetupBlacklistImportUrl,
  getChannelSetupUnsubscribeEntriesUrl,
  getChannelSetupUnsubscribeEntriesCountUrl,
  getChannelSetupOnDemandUrl,
  getChannelSetupProvidersUrl,
  getChannelSetupTemplatesUrl,
  getEmailTemplateByIdUrl,
  getEmailTemplateUpdateUrl,
  getEmailTemplateUploadUrl,
  getInviteEmailTemplateByIdUrl,
  getInviteEmailTemplateUploadUrl,
} from './channel-setup.config';
import {
  CatalogTemplate,
  CatalogTemplateChannel,
  ChannelBlacklistEntriesPage,
  ChannelBlacklistImportResult,
  ChannelBlacklistSetup,
  ChannelFilterTiming,
  ChannelUnsubscribeEntriesPage,
  ChannelSetupApiResponse,
  ChannelSetupConfig,
  ChannelSetupProviders,
} from './channel-setup.models';

function isApiSuccess(response: ChannelSetupApiResponse<unknown>): boolean {
  if (response?.success === true) {
    return true;
  }
  const status = Number(response?.status);
  return status === 0 || status === 200;
}

function unwrap<T>(response: ChannelSetupApiResponse<T> | Record<string, unknown>): T {
  const data = (response as ChannelSetupApiResponse<T>)?.data ?? response;
  if (data && typeof data === 'object' && 'template' in (data as object) && (data as { template?: T }).template) {
    return (data as { template: T }).template;
  }
  return data as T;
}

function pickUploadedTemplate(uploaded: Record<string, unknown> | null | undefined): {
  template_id?: string;
  gridfs_id?: string;
  file_name?: string;
} {
  const nested = uploaded?.['template'];
  const source = (nested && typeof nested === 'object'
    ? nested
    : uploaded) as Record<string, unknown> | undefined;
  const gridfs = source?.['gridfs_id'] ?? source?.['file_id'] ?? source?.['template_file_id'];
  const templateId = source?.['template_id'] ?? source?.['id'];
  const fileName = source?.['file_name'];
  return {
    template_id: templateId != null ? String(templateId) : undefined,
    gridfs_id: gridfs != null ? String(gridfs) : undefined,
    file_name: fileName != null ? String(fileName) : undefined,
  };
}

export function firstValidationMessage(error: unknown): string | null {
  const err = error as {
    status?: number;
    error?: { message?: string; data?: Record<string, unknown> };
    message?: string;
  };
  if (err?.status === 0) {
    return 'Could not reach the Channel Setup API. Confirm it is running, then retry.';
  }
  const data = err?.error?.data ?? {};
  const nested = data['validation_errors'] as { errors?: Array<{ message?: string }> } | undefined;
  const lists = [
    data['errors'],
    nested?.errors,
    nested,
    data['validationErrors'],
  ];
  for (const list of lists) {
    if (Array.isArray(list) && list[0]?.message) {
      return String(list[0].message);
    }
  }
  return err?.error?.message || err?.message || null;
}

@Injectable({ providedIn: 'root' })
export class ChannelSetupService {
  constructor(private readonly utilsService: UtilsService) {}

  getConfig(brandId: string): Observable<ChannelSetupConfig> {
    return this.getSetup(getChannelSetupConfigUrl(), brandId, 'Failed to load notification setup');
  }

  getOnDemand(brandId: string): Observable<ChannelSetupConfig> {
    return this.getSetup(getChannelSetupOnDemandUrl(), brandId, 'Failed to load on demand setup');
  }

  saveConfig(brandId: string, config: ChannelSetupConfig): Observable<ChannelSetupConfig> {
    const body = { ...config, brand_id: brandId } as Record<string, unknown>;
    delete body['notifications_enabled'];
    return this.putSetup(getChannelSetupConfigUrl(), brandId, body, 'Failed to save notification setup');
  }

  saveOnDemand(brandId: string, config: ChannelSetupConfig): Observable<ChannelSetupConfig> {
    const body = { ...config, brand_id: brandId } as Record<string, unknown>;
    delete body['notifications_enabled'];
    body['setup_mode'] = 'on_demand';
    return this.putSetup(getChannelSetupOnDemandUrl(), brandId, body, 'Failed to save on demand setup');
  }

  saveNotificationsEnabled(brandId: string, enabled: boolean): Observable<ChannelSetupConfig> {
    return this.putSetup(
      getChannelSetupConfigUrl(),
      brandId,
      { brand_id: brandId, notifications_enabled: enabled },
      'Failed to save notification setup',
    );
  }

  saveNotifyChannel(
    brandId: string,
    notifyChannel: string,
    communicationChannels?: string[],
  ): Observable<ChannelSetupConfig> {
    const body: Record<string, unknown> = { brand_id: brandId, notify_channel: notifyChannel };
    if (communicationChannels) {
      body['communication_channels'] = communicationChannels;
    }
    return this.putSetup(getChannelSetupConfigUrl(), brandId, body, 'Failed to save notification setup');
  }

  private getSetup(url: string, brandId: string, fallback: string): Observable<ChannelSetupConfig> {
    return this.utilsService
      .getAPICall(url, this.utilsService.setHeaderWithParams({ brand_id: brandId }))
      .pipe(
        map((response: ChannelSetupApiResponse<ChannelSetupConfig>) => {
          if (!isApiSuccess(response)) {
            throw new Error(response?.message ?? fallback);
          }
          return unwrap(response);
        }),
        catchError((error) => throwError(() => error ?? new Error(fallback))),
      );
  }

  private putSetup(
    url: string,
    brandId: string,
    body: Record<string, unknown>,
    fallback: string,
  ): Observable<ChannelSetupConfig> {
    return this.utilsService
      .putAPICall(url, body, this.utilsService.setHeaderWithParams({ brand_id: brandId }))
      .pipe(
        map((response: ChannelSetupApiResponse<ChannelSetupConfig>) => {
          if (!isApiSuccess(response)) {
            throw new Error(response?.message ?? fallback);
          }
          return unwrap(response);
        }),
        catchError((error) => throwError(() => error ?? new Error(fallback))),
      );
  }

  getProviders(brandId: string): Observable<ChannelSetupProviders> {
    const url = getChannelSetupProvidersUrl();
    return this.utilsService
      .getAPICall(url, this.utilsService.setHeaderWithParams({ brand_id: brandId }))
      .pipe(
        map((response: ChannelSetupApiResponse<ChannelSetupProviders>) => {
          if (!isApiSuccess(response)) {
            throw new Error(response?.message ?? 'Failed to load providers');
          }
          return unwrap(response);
        }),
        catchError((error) => throwError(() => error ?? new Error('Failed to load providers'))),
      );
  }

  uploadInviteEmailTemplate(params: {
    html: string;
    file_name: string;
    brand_id: string;
    setup_mode?: 'invites' | 'on_demand';
  }): Observable<{ template_file_id?: string; file_name?: string }> {
    const url = getInviteEmailTemplateUploadUrl();
    const body = {
      html: params.html,
      file_name: params.file_name,
      brand_id: params.brand_id,
      setup_mode: params.setup_mode,
    };
    return this.utilsService
      .postAPICall(url, body, this.utilsService.setHeaderWithParams({ brand_id: params.brand_id }))
      .pipe(
        map((response: ChannelSetupApiResponse<{
          _id?: string;
          file_id?: string;
          template_file_id?: string;
          file_name?: string;
        }>) => {
          if (!isApiSuccess(response)) {
            throw new Error(response?.message ?? 'Failed to upload email template');
          }
          const data = unwrap(response) as Record<string, unknown>;
          const fileId = data?.['template_file_id'] ?? data?.['_id'] ?? data?.['file_id'];
          const fileName = data?.['file_name'];
          return {
            template_file_id: fileId != null ? String(fileId) : undefined,
            file_name: fileName != null ? String(fileName) : undefined,
          };
        }),
        catchError((error) => throwError(() => error ?? new Error('Failed to upload email template'))),
      );
  }

  uploadEmailTemplate(params: {
    html: string;
    file_name: string;
    brand_id: string;
    template_id?: string;
  }): Observable<{ template_id?: string; gridfs_id?: string; file_name?: string }> {
    const isUpdate = Boolean(params.template_id);
    const url = isUpdate ? getEmailTemplateUpdateUrl() : getEmailTemplateUploadUrl();
    const body: Record<string, string> = {
      html: params.html,
      file_name: params.file_name,
      applied_on: 'project',
      unique_id: params.brand_id,
    };
    if (isUpdate && params.template_id) {
      body['template_id'] = params.template_id;
    }

    const request$ = isUpdate
      ? this.utilsService.putAPICall(url, body, this.utilsService.setHeaderWithParams({}))
      : this.utilsService.postAPICall(url, body, this.utilsService.setHeaderWithParams({}));

    return request$.pipe(
      map((response: ChannelSetupApiResponse<{ template_id?: string; gridfs_id?: string; file_name?: string }>) => {
        if (!isApiSuccess(response)) {
          throw new Error(response?.message ?? 'Failed to upload email template');
        }
        return pickUploadedTemplate(unwrap(response) as Record<string, unknown>);
      }),
      catchError((error) => throwError(() => error ?? new Error('Failed to upload email template'))),
    );
  }

  listCatalogTemplates(brandId: string, channel: CatalogTemplateChannel): Observable<CatalogTemplate[]> {
    const url = getChannelSetupTemplatesUrl();
    return this.utilsService
      .getAPICall(url, this.utilsService.setHeaderWithParams({ brand_id: brandId, channel }))
      .pipe(
        map((response: ChannelSetupApiResponse<{ templates?: CatalogTemplate[] }>) => {
          if (!isApiSuccess(response)) {
            throw new Error(response?.message ?? 'Failed to load templates');
          }
          const data = unwrap(response) as { templates?: CatalogTemplate[] };
          return Array.isArray(data?.templates) ? data.templates : [];
        }),
        catchError((error) => throwError(() => error ?? new Error('Failed to load templates'))),
      );
  }

  saveCatalogTemplate(
    brandId: string,
    payload: Record<string, unknown> & { channel: CatalogTemplateChannel; id?: string },
  ): Observable<CatalogTemplate> {
    const url = getChannelSetupTemplatesUrl();
    const body = { ...payload, brand_id: brandId };
    const isUpdate = Boolean(payload.id);
    const request$ = isUpdate
      ? this.utilsService.putAPICall(url, body, this.utilsService.setHeaderWithParams({ brand_id: brandId }))
      : this.utilsService.postAPICall(url, body, this.utilsService.setHeaderWithParams({ brand_id: brandId }));
    return request$.pipe(
      map((response: ChannelSetupApiResponse<CatalogTemplate>) => {
        if (!isApiSuccess(response)) {
          throw new Error(response?.message ?? 'Failed to save template');
        }
        return unwrap(response);
      }),
      catchError((error) => throwError(() => error ?? new Error('Failed to save template'))),
    );
  }

  deleteCatalogTemplate(brandId: string, channel: CatalogTemplateChannel, id: string): Observable<void> {
    const url = getChannelSetupTemplatesUrl();
    return this.utilsService
      .deleteAPICall(url, {}, this.utilsService.setHeaderWithParams({ brand_id: brandId, channel, id }))
      .pipe(
        map((response: ChannelSetupApiResponse<unknown>) => {
          if (!isApiSuccess(response)) {
            throw new Error(response?.message ?? 'Failed to delete template');
          }
        }),
        catchError((error) => throwError(() => error ?? new Error('Failed to delete template'))),
      );
  }

  getFilterTiming(brandId: string): Observable<ChannelFilterTiming> {
    const url = getChannelSetupFilterTimingUrl();
    return this.utilsService
      .getAPICall(url, this.utilsService.setHeaderWithParams({ brand_id: brandId }))
      .pipe(
        map((response: ChannelSetupApiResponse<ChannelFilterTiming>) => {
          if (!isApiSuccess(response)) {
            throw new Error(response?.message ?? 'Failed to load filters and timing');
          }
          return unwrap(response);
        }),
        catchError((error) => throwError(() => error ?? new Error('Failed to load filters and timing'))),
      );
  }

  saveFilterTiming(brandId: string, payload: Partial<ChannelFilterTiming>): Observable<ChannelFilterTiming> {
    const url = getChannelSetupFilterTimingUrl();
    const body: Record<string, unknown> = { brand_id: brandId };
    Object.entries(payload).forEach(([key, value]) => {
      if (value !== undefined) {
        body[key] = value;
      }
    });
    return this.utilsService
      .putAPICall(url, body, this.utilsService.setHeaderWithParams({ brand_id: brandId }))
      .pipe(
        map((response: ChannelSetupApiResponse<ChannelFilterTiming>) => {
          if (!isApiSuccess(response)) {
            throw new Error(response?.message ?? 'Failed to save filters and timing');
          }
          return unwrap(response);
        }),
        catchError((error) => throwError(() => error ?? new Error('Failed to save filters and timing'))),
      );
  }

  getBlacklist(brandId: string): Observable<ChannelBlacklistSetup> {
    const url = getChannelSetupBlacklistUrl();
    return this.utilsService
      .getAPICall(url, this.utilsService.setHeaderWithParams({ brand_id: brandId }))
      .pipe(
        map((response: ChannelSetupApiResponse<ChannelBlacklistSetup>) => {
          if (!isApiSuccess(response)) {
            throw new Error(response?.message ?? 'Failed to load Blacklist / DND');
          }
          return unwrap(response);
        }),
        catchError((error) => throwError(() => error ?? new Error('Failed to load Blacklist / DND'))),
      );
  }

  saveBlacklist(brandId: string, payload: Partial<ChannelBlacklistSetup>): Observable<ChannelBlacklistSetup> {
    const url = getChannelSetupBlacklistUrl();
    const body: Record<string, unknown> = { brand_id: brandId };
    Object.entries(payload).forEach(([key, value]) => {
      if (value !== undefined) {
        body[key] = value;
      }
    });
    return this.utilsService
      .putAPICall(url, body, this.utilsService.setHeaderWithParams({ brand_id: brandId }))
      .pipe(
        map((response: ChannelSetupApiResponse<ChannelBlacklistSetup>) => {
          if (!isApiSuccess(response)) {
            throw new Error(response?.message ?? 'Failed to save Blacklist / DND');
          }
          return unwrap(response);
        }),
        catchError((error) => throwError(() => error ?? new Error('Failed to save Blacklist / DND'))),
      );
  }

  listBlacklistEntries(
    brandId: string,
    params: { key?: string; q?: string; page?: number; page_size?: number } = {},
  ): Observable<ChannelBlacklistEntriesPage> {
    const url = getChannelSetupBlacklistEntriesUrl();
    const query: Record<string, string | number> = { brand_id: brandId };
    if (params.key) {
      query['key'] = params.key;
    }
    if (params.q) {
      query['q'] = params.q;
    }
    if (params.page != null) {
      query['page'] = String(params.page);
    }
    if (params.page_size != null) {
      query['page_size'] = String(params.page_size);
    }
    return this.utilsService
      .getAPICall(url, this.utilsService.setHeaderWithParams(query))
      .pipe(
        map((response: ChannelSetupApiResponse<ChannelBlacklistEntriesPage>) => {
          if (!isApiSuccess(response)) {
            throw new Error(response?.message ?? 'Failed to load Blacklist / DND values');
          }
          return unwrap(response);
        }),
        catchError((error) => throwError(() => error ?? new Error('Failed to load Blacklist / DND values'))),
      );
  }

  addBlacklistEntry(brandId: string, payload: { key: string; value: string }): Observable<unknown> {
    const url = getChannelSetupBlacklistEntriesUrl();
    return this.utilsService
      .postAPICall(url, { brand_id: brandId, ...payload }, this.utilsService.setHeaderWithParams({ brand_id: brandId }))
      .pipe(
        map((response: ChannelSetupApiResponse<unknown>) => {
          if (!isApiSuccess(response)) {
            throw new Error(response?.message ?? 'Failed to add Blacklist / DND value');
          }
          return unwrap(response);
        }),
        catchError((error) => throwError(() => error ?? new Error('Failed to add Blacklist / DND value'))),
      );
  }

  importBlacklistEntries(
    brandId: string,
    rows: Array<{ key: string; value: string }>,
  ): Observable<ChannelBlacklistImportResult> {
    const url = getChannelSetupBlacklistImportUrl();
    return this.utilsService
      .postAPICall(url, { brand_id: brandId, rows }, this.utilsService.setHeaderWithParams({ brand_id: brandId }))
      .pipe(
        map((response: ChannelSetupApiResponse<ChannelBlacklistImportResult>) => {
          if (!isApiSuccess(response)) {
            throw new Error(response?.message ?? 'Failed to import Blacklist / DND values');
          }
          return unwrap(response);
        }),
        catchError((error) => throwError(() => error ?? new Error('Failed to import Blacklist / DND values'))),
      );
  }

  deleteBlacklistEntry(brandId: string, id: string): Observable<void> {
    const url = getChannelSetupBlacklistEntriesUrl();
    return this.utilsService
      .deleteAPICall(url, {}, this.utilsService.setHeaderWithParams({ brand_id: brandId, id }))
      .pipe(
        map((response: ChannelSetupApiResponse<unknown>) => {
          if (!isApiSuccess(response)) {
            throw new Error(response?.message ?? 'Failed to remove Blacklist / DND value');
          }
        }),
        catchError((error) => throwError(() => error ?? new Error('Failed to remove Blacklist / DND value'))),
      );
  }

  listUnsubscribeEntries(
    brandId: string,
    params: { q?: string; search_field?: string; page?: number; page_size?: number } = {},
  ): Observable<ChannelUnsubscribeEntriesPage> {
    const url = getChannelSetupUnsubscribeEntriesUrl();
    const query: Record<string, string | number> = { brand_id: brandId };
    if (params.q) {
      query['q'] = params.q;
    }
    if (params.search_field) {
      query['search_field'] = params.search_field;
    }
    if (params.page != null) {
      query['page'] = String(params.page);
    }
    if (params.page_size != null) {
      query['page_size'] = String(params.page_size);
    }
    return this.utilsService
      .getAPICall(url, this.utilsService.setHeaderWithParams(query))
      .pipe(
        map((response: ChannelSetupApiResponse<ChannelUnsubscribeEntriesPage>) => {
          if (!isApiSuccess(response)) {
            throw new Error(response?.message ?? 'Failed to load unsubscribe list');
          }
          return unwrap(response);
        }),
        catchError((error) => throwError(() => error ?? new Error('Failed to load unsubscribe list'))),
      );
  }

  countUnsubscribeEntries(brandId: string): Observable<{ total: number }> {
    const url = getChannelSetupUnsubscribeEntriesCountUrl();
    return this.utilsService
      .getAPICall(url, this.utilsService.setHeaderWithParams({ brand_id: brandId }))
      .pipe(
        map((response: ChannelSetupApiResponse<{ total: number }>) => {
          if (!isApiSuccess(response)) {
            throw new Error(response?.message ?? 'Failed to load unsubscribe count');
          }
          return unwrap(response);
        }),
        catchError((error) => throwError(() => error ?? new Error('Failed to load unsubscribe count'))),
      );
  }

  deleteUnsubscribeEntry(brandId: string, id: string): Observable<void> {
    const url = getChannelSetupUnsubscribeEntriesUrl();
    return this.utilsService
      .deleteAPICall(url, {}, this.utilsService.setHeaderWithParams({ brand_id: brandId, id }))
      .pipe(
        map((response: ChannelSetupApiResponse<unknown>) => {
          if (!isApiSuccess(response)) {
            throw new Error(response?.message ?? 'Failed to remove unsubscribe row');
          }
        }),
        catchError((error) => throwError(() => error ?? new Error('Failed to remove unsubscribe row'))),
      );
  }

  getEmailTemplate(templateId: string): Observable<{
    html?: string;
    file_name?: string;
    template_id?: string;
    template_file_id?: string;
    gridfs_id?: string;
  }> {
    const url = getEmailTemplateByIdUrl();
    return this.utilsService
      .getAPICall(url, this.utilsService.setHeaderWithParams({ template_id: templateId }))
      .pipe(
        map((response: ChannelSetupApiResponse<{
          html?: string;
          file_name?: string;
          template_id?: string;
          gridfs_id?: string;
        }>) => {
          if (!isApiSuccess(response)) {
            throw new Error(response?.message ?? 'Failed to load email template');
          }
          return unwrap(response);
        }),
        catchError((error) => throwError(() => error ?? new Error('Failed to load email template'))),
      );
  }

  getInviteEmailTemplate(
    brandId: string,
    fileId: string,
  ): Observable<{
    html?: string;
    file_name?: string;
    template_id?: string;
    template_file_id?: string;
    gridfs_id?: string;
  }> {
    const url = getInviteEmailTemplateByIdUrl();
    return this.utilsService
      .getAPICall(url, this.utilsService.setHeaderWithParams({ brand_id: brandId, file_id: fileId }))
      .pipe(
        map((response: ChannelSetupApiResponse<{
          html?: string;
          file_name?: string;
          template_file_id?: string;
        }>) => {
          if (!isApiSuccess(response)) {
            throw new Error(response?.message ?? 'Failed to load email template');
          }
          return unwrap(response);
        }),
        catchError((error) => throwError(() => error ?? new Error('Failed to load email template'))),
      );
  }
}
